import { toThousands } from '@patsnap/synapse_common_utils'
import { IS_CN_REGION } from '@pharmsnap/pharmsnap-web/config'
import { computed, defineComponent, onMounted, ref } from '@vue/composition-api'
import { GDialog } from 'pharmsnapMF_shared/components'
import { useLocale } from 'pharmsnapMF_shared/composition'
import { sharedCtx } from 'pharmsnapMF_shared/context'
import { showSingleToast } from 'pharmsnapMF_shared/utils'
import creditsWelcomeBgCn from './assets/credits-benefit-cn.png'
import creditsWelcomeBgEn from './assets/credits-benefit-en.png'
import cn from './locales/cn.json'
import en from './locales/en.json'
import $style from './WelcomeCreditsDialog.module.scss'

const DEFAULT_CREDITS_AMOUNT = 20000
const ACTIVITY_CODE = 'eureka_gift'
const PLATFORM = 'web'
const SOURCE = 'plg_30d_activity_grant'
const ALREADY_CLAIMED_ERROR_CODE = 7400002
const CREDITS_VALID_DAYS = 30

export const WelcomeCreditsDialog = defineComponent({
  name: 'WelcomeCreditsDialog',
  i18n: {
    messages: {
      cn,
      en,
    },
  },
  setup() {
    const visible = ref(false)
    const loading = ref(false)
    const creditsAmount = ref(DEFAULT_CREDITS_AMOUNT)
    const { isCN, ts } = useLocale()

    const backgroundImage = computed(() => (isCN.value ? creditsWelcomeBgCn : creditsWelcomeBgEn))
    const formattedCreditsAmount = computed(() => toThousands(creditsAmount.value))

    const getExpirationDate = () => {
      const expirationDate = new Date(Date.now() + CREDITS_VALID_DAYS * 24 * 60 * 60 * 1000)
      if (isCN.value) {
        return `${expirationDate.getMonth() + 1}月${expirationDate.getDate()}日`
      }

      return expirationDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
      })
    }

    const showClaimSuccess = () => {
      showSingleToast({
        type: 'success',
        message: ts('WelcomeCreditsDialog.claimSuccess', {
          amount: formattedCreditsAmount.value,
          date: getExpirationDate(),
        }),
      })
    }

    /**
     * 领取积分
     */
    const claimCredits = async () => {
      if (loading.value) {
        return
      }

      loading.value = true
      try {
        const res = await sharedCtx.service.credits.rechargeActivityCredits({
          activityCode: ACTIVITY_CODE,
          platform: PLATFORM,
          source: SOURCE,
        })
        if (res.success) {
          visible.value = false
          showClaimSuccess()
          return
        }

        // 重复领取或当前仍有可用活动积分
        if (res.data.errorCode === ALREADY_CLAIMED_ERROR_CODE) {
          visible.value = false
          return
        }

        showSingleToast({ type: 'error', message: res.data.errorMsg || ts('WelcomeCreditsDialog.claimFailed') })
      } finally {
        loading.value = false
      }
    }

    /**
     * 检查欢迎积分资格
     */
    const checkWelcomeCredits = async () => {
      try {
        const res = await sharedCtx.service.credits.getRechargeInfo()

        if (!res.success) {
          return
        }

        const eligibility = res.data
        if (eligibility.has_pay_packages || !eligibility.is_personal_account) {
          return
        }

        if (!eligibility.has_recharge_records) {
          visible.value = true
          return
        }

        if (eligibility.is_latest_recharge_over_30_days) {
          await claimCredits()
        }
      } catch {
        return
      }
    }

    onMounted(() => {
      if (IS_CN_REGION) {
        return
      }

      checkWelcomeCredits()
    })

    return {
      visible,
      loading,
      backgroundImage,
      formattedCreditsAmount,
      creditsAmount,
      claimCredits,
    }
  },
  render() {
    return (
      <GDialog
        append-to-body
        destroy-on-close
        emptyMode
        showClose={false}
        showFooter={false}
        close-on-click-modal={false}
        close-on-press-escape={false}
        visible={this.visible}
        width="600px"
        customClass={$style.welcomeCreditsDialog}
      >
        <div>
          <div class={$style.banner} style={{ backgroundImage: `url(${this.backgroundImage})` }}>
            <h2 class={$style.title}>
              {this.$t('WelcomeCreditsDialog.title', {
                amount: this.formattedCreditsAmount,
              })}
            </h2>
          </div>
          <div class={$style.body}>
            <div class={$style.desc}>
              {this.$t('WelcomeCreditsDialog.desc', {
                amount: this.formattedCreditsAmount,
                days: CREDITS_VALID_DAYS,
              })}
            </div>
            <button
              class={['pt-ui-btn', $style.claimBtn]}
              type="button"
              data-size="big"
              data-type="submit"
              disabled={this.loading}
              onClick={this.claimCredits}
            >
              {this.$t('WelcomeCreditsDialog.claimBtn')}
            </button>
          </div>
        </div>
      </GDialog>
    )
  },
})
